import type { HotelPmsExportKey, HotelPmsPermissions, HotelPmsScreenKey } from '@/models/hotel-pms-permission'
import { HotelPmsScreenAccess } from '@/models/hotel-pms-permission'
import type { AccountType } from '@/libs/account'
import { hasCleanerRole, hasManagerRole } from '@/libs/account'

type PermissionUnknownPolicy = 'allow' | 'deny'
export const HOTEL_PMS_PERMISSION_SNAPSHOT_KEY = 'hotelPmsPermissionSnapshot'

export class HotelPmsReadOnlyGuardCancelledError extends Error {
  readonly isHotelPmsReadOnlyGuardCancelled = true

  constructor() {
    super('Hotel PMS read-only guard cancelled')
    this.name = 'HotelPmsReadOnlyGuardCancelledError'
  }
}

type HotelPmsPermissionSnapshot = {
  permissions?: HotelPmsPermissions
  loaded: boolean
  error: boolean
}

let inMemoryPermissionSnapshot: HotelPmsPermissionSnapshot | undefined
let cleanerBypassForWriteGuard = false
let managerBypassForPermissionUi = false
let permissionsPendingForWriteGuard = true

const PATH_SCREEN_RULES: Array<{ test: RegExp; screen: HotelPmsScreenKey }> = [
  { test: /^\/dashboard(?:\/|$)/, screen: 'dashboard' },
  {
    test: /^\/(accommodation-management|checkin|stay|checkout|entire-search|reservation|csv-upload-history|csv-download-history)(?:\/|$)/,
    screen: 'reservation',
  },
  { test: /^\/customer(?:\/|$)/, screen: 'customer' },
  { test: /^\/room-manager(?:\/|$)/, screen: 'guest_room' },
  { test: /^\/space-manager(?:\/|$)/, screen: 'reservation' },
  { test: /^\/(sales-manager|coupons-manager)(?:\/|$)/, screen: 'sales' },
  { test: /^\/(accounts-receivable-manager|invoice-manager)(?:\/|$)/, screen: 'accounts_receivable' },
  { test: /^\/(cleaning-manager|cleaning)(?:\/|$)/, screen: 'cleaning' },
  { test: /^\/mail-delivery-history(?:\/|$)/, screen: 'mail_sms' },
  { test: /^\/reports-manager(?:\/|$)/, screen: 'report' },
  { test: /^\/survey(?:\/|$)/, screen: 'survey' },
  { test: /^\/operation-log(?:\/|$)/, screen: 'operation_log' },
  { test: /^\/setting(?:\/|$)/, screen: 'settings' },
  { test: /^\/request-manager(?:\/|$)/, screen: 'reservation' },
  { test: /^\/restaurant-manager(?:\/|$)/, screen: 'restaurant' },
  { test: /^\/(mobile-menu|tablet-register|qr-checkin)(?:\/|$)/, screen: 'dashboard' },
]

const STAFF_FALLBACK_ORDER: Array<{ path: string; screen: HotelPmsScreenKey }> = [
  { path: '/dashboard', screen: 'dashboard' },
  { path: '/accommodation-management', screen: 'reservation' },
  { path: '/customer/list', screen: 'customer' },
  { path: '/room-manager/daily', screen: 'guest_room' },
  { path: '/sales-manager', screen: 'sales' },
  { path: '/accounts-receivable-manager', screen: 'accounts_receivable' },
  { path: '/cleaning-manager/status', screen: 'cleaning' },
  { path: '/mail-delivery-history', screen: 'mail_sms' },
  { path: '/reports-manager', screen: 'report' },
  { path: '/survey/summary', screen: 'survey' },
  { path: '/operation-log', screen: 'operation_log' },
  { path: '/setting/account', screen: 'settings' },
  { path: '/restaurant-manager', screen: 'restaurant' },
]

export const getScreenKeyByPathname = (pathname: string): HotelPmsScreenKey | undefined => {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  const matched = PATH_SCREEN_RULES.find(rule => rule.test.test(normalized))
  return matched?.screen
}

export const getScreenAccess = (
  permissions: HotelPmsPermissions | undefined,
  screenKey: HotelPmsScreenKey | undefined,
): HotelPmsScreenAccess | undefined => {
  if (!permissions || !screenKey) return undefined
  return permissions.screens?.[screenKey]
}

export const isScreenDenied = (
  permissions: HotelPmsPermissions | undefined,
  screenKey: HotelPmsScreenKey | undefined,
  unknownPolicy: PermissionUnknownPolicy = 'deny',
): boolean => {
  if (!permissions || !screenKey) {
    return unknownPolicy === 'deny'
  }
  const access = getScreenAccess(permissions, screenKey)
  if (!access) {
    return unknownPolicy === 'deny'
  }
  return access === HotelPmsScreenAccess.DENY
}

export const canEditScreen = (
  permissions: HotelPmsPermissions | undefined,
  screenKey: HotelPmsScreenKey | undefined,
  unknownPolicy: PermissionUnknownPolicy = 'deny',
): boolean => {
  const access = getScreenAccess(permissions, screenKey)
  if (!access) {
    return unknownPolicy === 'allow'
  }
  return access === HotelPmsScreenAccess.EDIT
}

export const shouldBypassHotelPmsPermissionUi = (): boolean => {
  return managerBypassForPermissionUi
}

/** permissions ロード前は read-only と判定しない（誤ブロック防止）。 */
export const isScreenReadOnlyForUi = (
  permissions: HotelPmsPermissions | undefined,
  screenKey: HotelPmsScreenKey | undefined,
  permissionsLoaded: boolean,
  permissionsError: boolean,
): boolean => {
  if (shouldBypassHotelPmsPermissionUi()) return false
  if (!permissionsLoaded || permissionsError) return false
  return !canEditScreen(permissions, screenKey)
}

export const guardScreenEditOrAlert = (
  permissions: HotelPmsPermissions | undefined,
  screenKey: HotelPmsScreenKey,
  permissionsLoaded: boolean,
  permissionsError: boolean,
  t: (key: string) => string,
): boolean => {
  if (shouldBypassHotelPmsPermissionUi()) return true
  if (!permissionsLoaded) return true
  if (permissionsError) {
    alert(t('HotelPms.Error.contextMissing'))
    return false
  }
  if (canEditScreen(permissions, screenKey)) return true
  alert(t('HotelPms.Error.readOnly'))
  return false
}

export const canUseOperation = (
  permissions: HotelPmsPermissions | undefined,
  operationKey: string,
  unknownPolicy: PermissionUnknownPolicy = 'deny',
): boolean => {
  if (!permissions) {
    return unknownPolicy === 'allow'
  }
  return permissions.operations?.[operationKey] === true
}

export const findFirstAllowedStaffPath = (permissions: HotelPmsPermissions | undefined): string | undefined => {
  const found = STAFF_FALLBACK_ORDER.find(item => !isScreenDenied(permissions, item.screen))
  return found?.path
}

/**
 * CSV エクスポート可否判定（exports の実値を参照。ロール名では判定しない）。
 * 権限不明時は既定で拒否（fail-safe）。
 */
export const canExportCsv = (
  permissions: HotelPmsPermissions | undefined,
  exportKey: HotelPmsExportKey,
  unknownPolicy: PermissionUnknownPolicy = 'deny',
): boolean => {
  if (!permissions) {
    return unknownPolicy === 'allow'
  }
  return permissions.exports?.[exportKey] === true
}

export type EnsureCsvExportPermissionOptions = {
  permissionsLoaded?: boolean
  permissionsError?: boolean
}

/**
 * CSV（および同一 UI ブロックのエクスポート）実行前のガード。
 * 仕様: ボタン表示は変えず、権限がない場合のみアラート（HotelPms.Error.exportDenied）。
 * @returns 実行してよいとき true、拒否したとき false
 */
export const ensureCsvExportPermissionOrAlert = (
  permissions: HotelPmsPermissions | undefined,
  t: (key: string) => string,
  exportKey: HotelPmsExportKey,
  options?: EnsureCsvExportPermissionOptions,
): boolean => {
  if (shouldBypassHotelPmsPermissionUi()) return true
  if (options?.permissionsError) {
    alert(t('HotelPms.Error.contextMissing'))
    return false
  }
  if (options?.permissionsLoaded === false) {
    return false
  }
  if (canExportCsv(permissions, exportKey, 'deny')) return true
  alert(t('HotelPms.Error.exportDenied'))
  return false
}

export type HotelPmsPermissionGuardSyncParams = {
  account?: AccountType
  permissions?: HotelPmsPermissions
  permissionsLoaded: boolean
  permissionsError: boolean
}

/** AccountContext から http 書き込みガード用の状態を同期（sessionStorage 不可時は in-memory にフォールバック）。 */
export const syncHotelPmsPermissionGuardState = ({
  account,
  permissions,
  permissionsLoaded,
  permissionsError,
}: HotelPmsPermissionGuardSyncParams): void => {
  cleanerBypassForWriteGuard = hasCleanerRole(account)
  managerBypassForPermissionUi = hasManagerRole(account)
  permissionsPendingForWriteGuard = !permissionsLoaded && !permissionsError

  if (!account || cleanerBypassForWriteGuard || permissionsPendingForWriteGuard) {
    inMemoryPermissionSnapshot = undefined
    removeStoredHotelPmsPermissionSnapshot()
    return
  }

  const snapshot: HotelPmsPermissionSnapshot = {
    permissions,
    loaded: permissionsLoaded,
    error: permissionsError,
  }
  inMemoryPermissionSnapshot = snapshot
  setStoredHotelPmsPermissionSnapshot(snapshot)
}

/** cleaner / 管理者は従来どおり書き込みガードをスキップ。permissions ロード中もスキップする。 */
export const shouldSkipHotelPmsWriteGuard = (): boolean => {
  return cleanerBypassForWriteGuard || managerBypassForPermissionUi || permissionsPendingForWriteGuard
}

export const getStoredHotelPmsPermissionSnapshot = (): HotelPmsPermissionSnapshot | undefined => {
  try {
    const raw = sessionStorage.getItem(HOTEL_PMS_PERMISSION_SNAPSHOT_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as HotelPmsPermissionSnapshot
      if (typeof parsed?.loaded === 'boolean' && typeof parsed?.error === 'boolean') {
        return parsed
      }
    }
  } catch (_error) {
    // sessionStorage 不可時は in-memory にフォールバック
  }
  return inMemoryPermissionSnapshot
}

export const setStoredHotelPmsPermissionSnapshot = (snapshot: HotelPmsPermissionSnapshot): void => {
  inMemoryPermissionSnapshot = snapshot
  try {
    sessionStorage.setItem(HOTEL_PMS_PERMISSION_SNAPSHOT_KEY, JSON.stringify(snapshot))
  } catch (_error) {
    // Safari プライベートモード等で storage が使えない場合は in-memory のみ
  }
}

export const removeStoredHotelPmsPermissionSnapshot = (): void => {
  inMemoryPermissionSnapshot = undefined
  try {
    sessionStorage.removeItem(HOTEL_PMS_PERMISSION_SNAPSHOT_KEY)
  } catch (_error) {
    // storage 無効時は何もしない
  }
}
